import { v4 as uuidv4 } from "uuid";
import { Descendant } from "slate";
import { CustomText } from "../components/Slate/CustomText";
import { MentionNode, ParagraphNode } from "../components/Slate/BaseNode";

export type BlockType =
  | "paragraph"
  | "heading-one"
  | "heading-two"
  | "heading-three";

// ✅ Creates a CustomText node (leaf node without children)
export const createTextNode = (text: string = ""): CustomText => ({
  id: uuidv4(),
  type: "text",
  text,
});

// ✅ Creates a ParagraphNode with `CustomText[]` as children
export const createParagraphNode = (text: string = ""): ParagraphNode => ({
  id: uuidv4(),
  type: "paragraph",
  children: [createTextNode(text)], // ✅ Only CustomText[]
});

// ✅ Creates a MentionNode with `CustomText[]` as children
export const createMentionNode = (
  id: string,
  character: string,
  mentionType: "user" | "file" | "event" | "participant",
): MentionNode => ({
  id: id,
  type: "mention",
  mentionType,
  character,
  children: [createTextNode(character)], // ✅ Only CustomText[]
});

// ✅ Ensure IDs exist for all nodes while keeping correct structure
export const ensureIds = (nodes: Descendant[]): Descendant[] =>
  nodes.map((node) => {
    if ("text" in node) {
      // ✅ If it's a leaf node (CustomText), assign a unique ID if missing
      return {
        ...node,
        id: node.id && typeof node.id === "string" ? node.id : uuidv4(), // Ensure unique ID
      } as CustomText;
    } else {
      // ✅ If it's a block element (paragraph or mention), assign a new ID if missing
      return {
        ...node,
        id: uuidv4(), // ALWAYS generate a new unique ID per node
        children: node.children
          ? (node.children.map((child) =>
              "text" in child
                ? { ...child, id: uuidv4() } // ✅ Ensure child text nodes have unique IDs
                : createTextNode(),
            ) as CustomText[])
          : [createTextNode()], // Default to a text node if empty
      } as ParagraphNode | MentionNode;
    }
  });
